import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectSelectedTake3D } from "../../../app/slices/TrainingSlice";
import {
  IMessageBubble,
  messageBubbleStyles,
  requiredMessageBubbleProperties,
} from "../../../models/interactions/MessageBubble";
import { UpdatePropertyOfInteraction } from "../../../app/slices/helpers/TrainingSliceHelper";
import Interaction from "../../../models/interactions/Interaction";
import NodeActorPropDropdown from "../components/NodeActorPropDropdown";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import SequenceLocalizedRichTextField from "../components/SequenceLocalizedRichTextField";
import NodeNumberField from "../components/NodeNumberField";
import { InteractionProperty } from "../../../models/InteractionProperty";
import MessageBubbleAnimationDropdown from "../../previews/message_bubble/MessageBubbleAnimationDropdown";
import LookatTargetDropdown from "./LookatTargetDropdown";
import { useTranslation } from "react-i18next";
import usePropHelper from "../../../hooks/PropHelper";
import NodeStringDropdown from "../components/NodeStringDropdown";
import { getActorPositionIdInTake } from "../EnvironmentPositionContext";
import { hasLookatTarget } from "../../../models/NoLookat";

export default function MessageBubbleNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const { t } = useTranslation();
  const messageBubble = props.interaction as IMessageBubble;
  const { updateInteraction, sequence } = useUpdateSequenceInteraction();
  const take = useSelector(selectSelectedTake3D);
  const { getBubbleTargetProps } = usePropHelper();
  const propsInTake = useMemo(
    () =>
      take
        ? getBubbleTargetProps(take, {
            kind: "sequence",
            nodeId: props.nodeId,
            sequence,
          })
        : [],
    [getBubbleTargetProps, props.nodeId, sequence, take]
  );
  const isPropIdInTake = (objectId: string) =>
    propsInTake.some((prop) => prop.id === objectId);
  const isPropAnchor = isPropIdInTake(messageBubble.npcId);
  // In sequences lookAt is a position id; the anchor's own position is never a valid target.
  const isSelfLookAt =
    !!messageBubble.lookAt &&
    messageBubble.lookAt === getActorPositionIdInTake(take, messageBubble.npcId);

  useEffect(() => {
    if (!messageBubble.lookAt) return;
    if (!isPropAnchor && !isSelfLookAt) return;
    updateInteraction(props.nodeId, {
      ...messageBubble,
      lookAt: "",
    } as IMessageBubble);
  }, [isPropAnchor, isSelfLookAt, messageBubble, props.nodeId, updateInteraction]);

  const actorDropdown = useMemo(
    () => (
      <NodeActorPropDropdown
        label="Actor"
        selectedObjectId={messageBubble.npcId}
        availableProps={propsInTake}
        onClick={(objectId: string) => {
          const mustClearLookAt =
            isPropIdInTake(objectId) ||
            (!!messageBubble.lookAt &&
              messageBubble.lookAt === getActorPositionIdInTake(take, objectId));
          updateInteraction(props.nodeId, {
            ...messageBubble,
            npcId: objectId,
            lookAt: mustClearLookAt ? "" : messageBubble.lookAt,
          } as IMessageBubble);
        }}
      />
    ),
    [messageBubble, propsInTake, take, updateInteraction, props.nodeId]
  );

  const lookAtDropdown = useMemo(
    () => (
      <LookatTargetDropdown
        label="Look at"
        nodeId={props.nodeId}
        selectedTargetId={messageBubble.lookAt}
        allowKeepCurrent
        allowNoActor
        disabled={isPropAnchor}
        excludeActorId={messageBubble.npcId}
        onClick={(targetId: string) =>
          updateInteraction(props.nodeId, {
            ...messageBubble,
            lookAt: targetId,
          } as IMessageBubble)
        }
      />
    ),
    [isPropAnchor, messageBubble, updateInteraction, props.nodeId]
  );

  // How the speaker looks at the listener: whole body (finalIK false, default) or head only.
  // Only shown when there is a listener and the speaker is an actor.
  const wholeBodyLabel = String(t("look_mode.whole_body"));
  const headOnlyLabel = String(t("look_mode.head_only"));
  const lookModeDropdown = useMemo(
    () =>
      !isPropAnchor && hasLookatTarget(messageBubble.lookAt) ? (
        <NodeStringDropdown
          label={String(t("look_mode.label"))}
          items={[wholeBodyLabel, headOnlyLabel]}
          selectedItem={messageBubble.finalIK ? headOnlyLabel : wholeBodyLabel}
          onClick={(item: string) =>
            updateInteraction(props.nodeId, {
              ...messageBubble,
              finalIK: item === headOnlyLabel,
            } as IMessageBubble)
          }
        />
      ) : null,
    [isPropAnchor, messageBubble, updateInteraction, props.nodeId, wholeBodyLabel, headOnlyLabel, t]
  );

  const animationDropdown = useMemo(
    () => (
      <MessageBubbleAnimationDropdown
        selectedAnimation={messageBubble.animation}
        variant="node"
        onClick={(animation) =>
          updateInteraction(props.nodeId, {
            ...messageBubble,
            animation: animation,
          } as IMessageBubble)
        }
      />
    ),
    [messageBubble.animation, updateInteraction, props.nodeId]
  );

  const messageStyleDropdown = useMemo(
    () => (
      <NodeStringDropdown
        label="Message Style"
        items={messageBubbleStyles}
        selectedItem={messageBubble.messageBubbleStyle}
        onClick={(messageBubbleStyle: string) =>
          updateInteraction(props.nodeId, {
            ...messageBubble,
            messageBubbleStyle,
          } as IMessageBubble)
        }
      />
    ),
    [messageBubble, props.nodeId, updateInteraction]
  );

  const displayTimeProperty = useMemo(() => {
    return messageBubble.properties?.find((property) => property.id === "displayTime");
  }, [messageBubble.properties]);

  const displayTimeInput = useMemo(
    () => (
      <NodeNumberField
        label="Display Time"
        value={(displayTimeProperty?.value as number) ?? 0}
        onChange={(displayTime) => {
          const baseProperty = displayTimeProperty ?? requiredMessageBubbleProperties.find((property) => property.id === "displayTime");
          if (!baseProperty) return;

          const updatedProperty: InteractionProperty = {
            ...baseProperty,
            value: displayTime,
          };

          const updatedInteraction = displayTimeProperty
            ? UpdatePropertyOfInteraction(messageBubble, updatedProperty)
            : ({
                ...messageBubble,
                properties: [...(messageBubble.properties ?? []), updatedProperty],
              } as IMessageBubble);

          updateInteraction(props.nodeId, updatedInteraction);
        }}
      />
    ),
    [displayTimeProperty, messageBubble, props.nodeId, updateInteraction]
  );

  const messageInput = useMemo(
    () => (
      <SequenceLocalizedRichTextField
        label="Message"
        placeholder="Message"
        localizationKey={messageBubble.text}
      />
    ),
    [messageBubble.text]
  );

  return (
    <div className="w-full h-full flex flex-col gap-[4px]">
      {actorDropdown}
      {lookAtDropdown}
      {lookModeDropdown}
      {animationDropdown}
      {messageStyleDropdown}
      {displayTimeInput}
      {messageInput}
    </div>
  );
}

